import { isEmpty } from '@sgme/fp';
import { unminifyError } from '@sgme/stacktrace';
import { TextArea } from '@sgme/ui';
import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Accordion, Modal } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import { SCREENSHOT_QUALITY, SUPPORT_EMAIL } from '@/components/fatality/const';
import { ErrorFromRedux } from '@/components/fatality/ErrorFromRedux.ts';
import { UsertypeGuard } from '@/components/UsertypeGuard.tsx';
import { getConfig } from '@/config/config.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import { take as takeScreenshot } from '@/utils/screenshot.ts';

type Props = {
  error: Error | ErrorFromRedux;
  onReload: () => void;
};

export const AppCrashModal = ({ error, onReload }: Props) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const [helpRequestStatus, setHelpRequestStatus] = useState<'unsent' | 'sending' | 'succeed' | 'failed'>('unsent');

  const [errorMessage, setErrorMessage] = useState('');
  const [details, setDetails] = useState('');
  const [beautifulStackTrace, setBeautifulStackTrace] = useState('');
  const [screenshot, setScreenshot] = useState(''); // base64 encoded jpeg format screenshot
  const [userProvidedContext, setUserProvidedContext] = useState('');

  useEffect(() => {
    //
    // Processing error and creating screenshot
    //
    let source = '';
    if (error.stack) {
      source = error.stack;
    } else {
      try {
        source = JSON.stringify(error);
      } catch (_) {}
    }

    let _details = `APP_CRASHED can't unminify stack for error: ${JSON.stringify(error)}`;

    unminifyError(error)
      .then((unminifiedError) => {
        const errorMessage = unminifiedError?.message;
        const beautifulStackTrace = unminifiedError?.stack;

        if (typeof errorMessage === 'string' && !isEmpty(errorMessage)) {
          setErrorMessage(errorMessage);
        }

        if (typeof beautifulStackTrace === 'string' && !isEmpty(beautifulStackTrace)) {
          setBeautifulStackTrace(beautifulStackTrace);
          if (typeof errorMessage === 'string' && !isEmpty(errorMessage)) {
            const causedByaction =
              error instanceof ErrorFromRedux && error.cause
                ? `\n\nDuring processing of action: ${JSON.stringify(error.cause)}`
                : '';
            _details = `Automatic help request sent after APP_CRASHED from: ${source}\n ${errorMessage}:\n${beautifulStackTrace}${causedByaction}`;
          }
        }
      })
      .finally(() => {
        setDetails(_details);
      });

    takeScreenshot({
      element: document.body,
      shouldIgnoreElement: (element: Element): boolean => {
        const { classList } = element;
        return (
          (classList &&
            // classList.contains('life-ring-modal-component') || // ?
            (classList.contains('modal') || classList.contains('modal-backdrop'))) ||
          element.getAttribute('role') === 'dialog'
        );
      },
    }).then((canvas) => {
      setScreenshot(canvas.toDataURL('image/jpeg', SCREENSHOT_QUALITY));
    });
  }, [error]);

  useEffect(() => {
    if (isEmpty(details) || isEmpty(screenshot)) {
      return;
    }

    dispatch(
      sendHelpRequestThunk({
        config: 'appcrashed',
        details,
        screenshot,
        templateId: getConfig().app.mailTemplateIds.templateIdForAppCrashed,
      }),
    );
  }, [details, screenshot]);

  const handleSubmit = useCallback(() => {
    if (isEmpty(details) || isEmpty(screenshot)) {
      return;
    }

    if (isEmpty(userProvidedContext)) {
      onReload();
      return;
    }

    setHelpRequestStatus('sending');
    dispatch(
      sendHelpRequestThunk({
        config: 'appcrashed',
        details: `${userProvidedContext}\n\n${details}`,
        screenshot,
        templateId: getConfig().app.mailTemplateIds.templateIdForAppCrashed,
      }),
    ).then(({ payload }) => {
      if (payload === 'succeed') {
        onReload();
      } else {
        setHelpRequestStatus('failed');
      }
    });
  }, [details, screenshot, userProvidedContext, onReload]);

  return (
    <>
      <div className="h-100" /> {/* having page layout ok even with no content */}
      <Modal show backdrop="static" size="lg">
        <Modal.Header>
          <h4 className="text-danger">
            <FormattedMessage id="fatal.appcrash.tile" />
          </h4>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-16px">
          <FormattedMessage id="fatal.appcrash.body" tagName="p" />
          {!isEmpty(error.message) && <p className="text-warning fw-bold">{error.message}</p>}
          <TextArea
            aria-label="userProvidedContext"
            placeholder={formatMessage({ id: 'fatal.appcrash.userProvidedContextPlaceholder' })}
            value={userProvidedContext}
            style={{ height: '150px' }}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setUserProvidedContext(e.target.value)}
          />
          <UsertypeGuard allow={['Internal Sales', 'Internal Trader']}>
            <Accordion flush>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <FormattedMessage id="fatal.appcrash.stackTrace" />
                </Accordion.Header>
                <Accordion.Body className="p-0">
                  <TextArea
                    disabled
                    aria-label="errorStackTrace"
                    value={`${errorMessage}\n${beautifulStackTrace}`}
                    style={{
                      height: '300px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre',
                      overflow: 'auto',
                    }}
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </UsertypeGuard>
        </Modal.Body>
        <Modal.Footer>
          {helpRequestStatus === 'succeed' && (
            <div className="text-success">
              <FormattedMessage id="fatal.helpRequestSendingSucceeded" />
            </div>
          )}
          {helpRequestStatus === 'failed' && (
            <div className="text-danger">
              <FormattedMessage
                id="fatal.helpRequestSendingFailed"
                values={{ mailLink: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> }}
              />
            </div>
          )}
          {(helpRequestStatus === 'unsent' || helpRequestStatus === 'sending') && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={helpRequestStatus === 'sending' || isEmpty(details) || isEmpty(screenshot)}
            >
              {helpRequestStatus === 'sending' && <div className="me-2 spinner spinner-sm" />}
              <FormattedMessage
                id={isEmpty(userProvidedContext) ? 'fatal.appcrash.reload' : 'fatal.appcrash.sendAndReload'}
              />
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
